// app.config.ts
import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideAnimations,
  provideNoopAnimations,
} from '@angular/platform-browser/animations';
import {
  TitleStrategy,
  provideRouter,
  withComponentInputBinding,
} from '@angular/router';

import { NGX_EDITOR_CONFIG_TOKEN, NgxEditorModule } from 'ngx-editor';
import {
  provideTranslateLoader,
  provideTranslateService,
  TranslateService,
} from '@ngx-translate/core';

import { Configuration } from './api';
import { routes } from './app.routes';
import { ApiConfiguration } from './config/api-configuration';
import {
  DEFAULT_LANGUAGE,
  I18nInitService,
  MultiTranslateHttpLoader,
} from './core/i18n';
import { I18nTitleStrategy } from './core/i18n/i18n-title.strategy';
import { GlobalErrorHandler, LoggerService } from './core/logging';
import {
  CsrfTokenInterceptor,
  ErrorLoggingInterceptor,
  PendingRequestsInterceptor,
  SessionExpiredInterceptor,
} from './shared/interceptors';
import { createEditorLocals } from './shared/config/editor-locals';
import { IdleLogoutService, SessionKeepaliveService } from './shared/service';

export function i18nInitFactory(service: I18nInitService): () => Promise<void> {
  return () => service.init();
}

// angular animations drive the web animations api, which the css media query
// in styles.scss cannot reach. the preference is read once at bootstrap; a
// change to the system setting takes effect on the next reload.
function prefersReducedMotion(): boolean {
  return (
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const apiConfiguration = new ApiConfiguration();

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: Configuration, useValue: apiConfiguration },
    provideTranslateService({
      fallbackLang: DEFAULT_LANGUAGE,
      loader: provideTranslateLoader(MultiTranslateHttpLoader),
    }),
    importProvidersFrom(NgxEditorModule.forRoot()),
    {
      provide: NGX_EDITOR_CONFIG_TOKEN,
      useFactory: (translate: TranslateService) => ({
        locals: createEditorLocals(translate),
      }),
      deps: [TranslateService],
    },
    provideHttpClient(
      withXhr(),
      withInterceptors([
        CsrfTokenInterceptor,
        SessionExpiredInterceptor,
        ErrorLoggingInterceptor,
        PendingRequestsInterceptor,
      ]),
    ),
    provideRouter(routes, withComponentInputBinding()),
    prefersReducedMotion() ? provideNoopAnimations() : provideAnimations(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: TitleStrategy, useClass: I18nTitleStrategy },
    provideAppInitializer(() => {
      const initializerFn = i18nInitFactory(inject(I18nInitService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = ((
        keepalive: SessionKeepaliveService,
        idleLogout: IdleLogoutService,
        logger: LoggerService,
      ): (() => void) => {
        return (): void => {
          void keepalive;
          void idleLogout;
          logger.info('Frontend logger initialized', { module: 'bootstrap' });
        };
      })(inject(SessionKeepaliveService), inject(IdleLogoutService), inject(LoggerService));
      return initializerFn();
    }),
  ],
};
